import React, { useState, useEffect } from 'react';
import { communicationAPI } from '../../services/api';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import StatusBadge from '../../components/common/StatusBadge';
import toast from 'react-hot-toast';
import { formatDate } from '../../utils/helpers';

const CommunicationModule = () => {
  const [tab, setTab] = useState('announcements');
  const [announcements, setAnnouncements] = useState([]);
  const [circulars, setCirculars] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', target_audience: 'all', priority: 'medium', publish_date: new Date().toISOString().split('T')[0] });

  useEffect(() => {
    loadAnnouncements();
    communicationAPI.circulars().then(r => setCirculars(r.data.results || r.data)).catch(() => {});
    communicationAPI.messages().then(r => setMessages(r.data.results || r.data)).catch(() => {});
  }, []);

  const loadAnnouncements = () => {
    setLoading(true);
    communicationAPI.announcements().then(r => setAnnouncements(r.data.results || r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await communicationAPI.createAnnouncement(form);
      toast.success('Announcement published!');
      setShowModal(false);
      loadAnnouncements();
      setForm({ title: '', content: '', target_audience: 'all', priority: 'medium', publish_date: new Date().toISOString().split('T')[0] });
    } catch { toast.error('Failed to publish'); }
  };

  const announcementColumns = [
    { key: 'title', label: 'Title', render: v => <strong>{v}</strong> },
    { key: 'target_audience', label: 'Audience', render: v => <span className="badge bg-info text-dark">{v}</span> },
    { key: 'priority', label: 'Priority', render: v => <span className={`badge ${v === 'high' ? 'bg-danger' : v === 'medium' ? 'bg-warning text-dark' : 'bg-secondary'}`}>{v}</span> },
    { key: 'publish_date', label: 'Date', render: v => formatDate(v) },
    { key: 'is_active', label: 'Status', render: v => <StatusBadge status={v ? 'active' : 'inactive'} /> },
  ];

  const circularColumns = [
    { key: 'circular_number', label: 'Circular No.', render: v => <code>{v}</code> },
    { key: 'title', label: 'Title', render: v => <strong>{v}</strong> },
    { key: 'issue_date', label: 'Issue Date', render: v => formatDate(v) },
    { key: 'is_active', label: 'Status', render: v => <StatusBadge status={v ? 'active' : 'inactive'} /> },
  ];

  return (
    <div>
      <div className="page-header">
        <div><h2>Communication</h2><p>Announcements, messages, circulars and notifications</p></div>
        {tab === 'announcements' && <button className="btn btn-primary" onClick={() => setShowModal(true)}>📢 New Announcement</button>}
        {tab === 'circulars' && <button className="btn btn-primary" onClick={() => setShowModal(true)}>📄 New Circular</button>}
      </div>

      <div className="d-flex gap-2 mb-4 flex-wrap">
        {[['announcements','📢 Announcements'],['notifications','🔔 Notifications']].map(([k,l]) => (
          <button key={k} className={`btn ${tab === k ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>
        {/*['messages','💬 Messages'], ['circulars','📄 Circulars'], */}
      {tab === 'announcements' && (
        <div className="card">
          <div className="card-header"><span>📢 Announcements ({announcements.length})</span></div>
          <DataTable columns={announcementColumns} data={announcements} loading={loading} />
        </div>
      )}

      {tab === 'messages' && (
        <div className="row g-3">
          <div className="col-md-4">
            <div className="card">
              <div className="card-header"><span>📬 Inbox</span><button className="btn btn-sm btn-primary">✉️ Compose</button></div>
              <div className="card-body p-0">
                {messages.length === 0 ? (
                  <div className="empty-state"><div className="empty-icon">💬</div><p>No messages</p></div>
                ) : messages.map(m => (
                  <div key={m.id} style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ fontWeight: m.is_read ? 400 : 700, fontSize: 13 }}>{m.sender_name}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{m.subject}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-md-8">
            <div className="card"><div className="card-body">
              <div className="empty-state"><div className="empty-icon">💬</div><h5>Select a message</h5><p>Click a message from the inbox to read it.</p></div>
            </div></div>
          </div>
        </div>
      )}

      {tab === 'circulars' && (
        <div className="card">
          <div className="card-header"><span>📄 Circulars & Notices ({circulars.length})</span></div>
          <DataTable columns={circularColumns} data={circulars} loading={false} />
        </div>
      )}

      {tab === 'notifications' && (
        <div className="card"><div className="card-body">
          <div className="row g-3">
            {[
              { icon: '📱', title: 'SMS Notifications', desc: 'Send bulk SMS to parents/students', color: '#eff6ff' },
              { icon: '📧', title: 'Email Notifications', desc: 'Send email alerts to recipients', color: '#f0fdf4' },
              { icon: '🔔', title: 'Push Notifications', desc: 'App push notifications', color: '#faf5ff' },
            ].map(n => (
              <div key={n.title} className="col-md-4">
                <div style={{ background: n.color, borderRadius: 12, padding: 20, textAlign: 'center' }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>{n.icon}</div>
                  <h6 style={{ fontWeight: 700 }}>{n.title}</h6>
                  <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{n.desc}</p>
                  <button className="btn btn-sm btn-primary mt-3">Configure</button>
                </div>
              </div>
            ))}
          </div>
        </div></div>
      )}

      <Modal show={showModal} onClose={() => setShowModal(false)} title="New Announcement" size="lg">
        <form onSubmit={handleSave}>
          <div className="mb-3"><label className="form-label">Title *</label><input className="form-control" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required /></div>
          <div className="mb-3"><label className="form-label">Content *</label><textarea className="form-control" rows={5} value={form.content} onChange={e => setForm({...form, content: e.target.value})} required /></div>
          <div className="row">
            <div className="col-md-4 mb-3"><label className="form-label">Target Audience</label>
              <select className="form-select" value={form.target_audience} onChange={e => setForm({...form, target_audience: e.target.value})}>
                <option value="all">All</option><option value="students">Students</option>
                <option value="staff">Staff</option><option value="parents">Parents</option>
              </select>
            </div>
            <div className="col-md-4 mb-3"><label className="form-label">Priority</label>
              <select className="form-select" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}>
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
            <div className="col-md-4 mb-3"><label className="form-label">Publish Date</label>
              <input type="date" className="form-control" value={form.publish_date} onChange={e => setForm({...form, publish_date: e.target.value})} />
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">📢 Publish</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
export default CommunicationModule;
